import React, { useContext } from 'react';
import { useQueryClient } from 'react-query';
import { History as ReleaseNoteHistory, ReleaseNotes } from 'views/App/Portal/Policy/_types/manuals/draft';
import ContentConversionHelper from '../../helpers/contentConversionHelper';

import useManagePolicy from '../../_data_sources/useManagePolicy';
import { DraftManualDetailContext } from '../context';
import useManagePreface from '../../_data_sources/useManagePreface';

type MinimalPolicy = { id: string; releaseNotes?: ReleaseNotes; };

interface UseReleaseNotes {
  data?: MinimalPolicy | null;
  invalidateQuery: string;
  type?: 'PREFACE' | 'POLICY';
}

const useReleaseNotes = ({ data, invalidateQuery, type = 'POLICY' }: UseReleaseNotes) => {
  const propData = data;
  const { manual } = useContext(DraftManualDetailContext);
  const [currentReleaseNoteIndex, setCurrentReleaseNoteIndex] = React.useState(0);

  const queryClient = useQueryClient();
  const { updateReleaseNotes: updatePolicyReleaseNotes, approveReleaseNotes: approvePolicyReleaseNotes } =
    useManagePolicy();
  const { updateReleaseNotes: updatePrefaceReleaseNotes, approveReleaseNotes: approvePrefaceReleaseNotes } =
    useManagePreface();

  const updateReleaseNotes = type === 'POLICY' ? updatePolicyReleaseNotes : updatePrefaceReleaseNotes;
  const approveReleaseNotes = type === 'POLICY' ? approvePolicyReleaseNotes : approvePrefaceReleaseNotes;

  const handleNoteIndexChange = (addNumber: number) => {
    const newIndex = currentReleaseNoteIndex + addNumber;
    if (propData?.releaseNotes && newIndex >= 0 && newIndex < propData.releaseNotes.history.length) {
      setCurrentReleaseNoteIndex(newIndex);
    }
  };

  const editNote = (newNote: ReleaseNoteHistory): ReleaseNoteHistory[] => {
    if (!propData?.releaseNotes) return [];
    const newHistory = [...propData.releaseNotes.history];
    newHistory[currentReleaseNoteIndex] = newNote;
    return newHistory;
  };

  const addNewNote = (newNote: ReleaseNoteHistory): ReleaseNoteHistory[] => {
    if (!propData?.releaseNotes) return [newNote];
    return [newNote, ...propData.releaseNotes.history];
  };

  const manageEditOrCreate = (data: ReleaseNoteHistory, mode: 'EDIT' | 'CREATE'): ReleaseNoteHistory[] => {
    if (mode === 'EDIT') {
      const currentNote = propData?.releaseNotes?.history[currentReleaseNoteIndex];
      const newData = {
        ...data,
        updatedAt: new Date().toISOString(),
        createdAt: currentNote ? currentNote.createdAt : new Date().toISOString(),
        version: currentNote ? currentNote.version : 1,
      };
      return editNote(newData);
    } else {
      const newNote = {
        ...data,
        version: (propData?.releaseNotes?.history.length || 0) + 1,
      };
      return addNewNote(newNote);
    }
  };

  const handleUpdateReleaseNotes = async ({
    data,
    mode,
  }: {
    data: { title: string; content: string; };
    mode: 'EDIT' | 'CREATE';
  }) => {
    if (manual && propData) {
      return new Promise<void>((resolve, reject) => {
        const defaultNote: ReleaseNoteHistory = {
          title: data.title,
          content: ContentConversionHelper.contentForRender(data.content),
          contentEditable: ContentConversionHelper.contentForEditor(data.content),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        };
        const newData = manageEditOrCreate(defaultNote, mode);
        updateReleaseNotes(
          manual.id,
          propData.id,
          {
            history: newData,
          },
          () => {
            queryClient.invalidateQueries([invalidateQuery, manual.id, propData.id]);
            if (mode === 'CREATE') {
              setCurrentReleaseNoteIndex(0);
            }
            resolve();
          },
          (error) => {
            reject(error);
          }
        );
      });
    }
  };

  const handleApproveReleaseNotes = async () => {
    if (manual && propData) {
      await approveReleaseNotes(
        manual.id,
        propData.id,
        () => {
          queryClient.invalidateQueries([invalidateQuery, manual.id, propData.id]);
        },
        (err: any) => {
          console.error('Error approving release notes:', err);
        }
      );
    }
  };

  return {
    currentReleaseNoteIndex,
    currentNote: propData?.releaseNotes?.history[currentReleaseNoteIndex],
    handleNoteIndexChange,
    handleUpdateReleaseNotes,
    handleApproveReleaseNotes,
    amountOfReleaseNotes: propData?.releaseNotes?.history.length || 0,
    notesStatus: propData?.releaseNotes?.draftState,
  };
};

export default useReleaseNotes;
